import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Wand2, Flame, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { DIFFICULTIES, saveProfile } from "@/data/practiceData";
import { getChapters, getSubjects } from "@/data/curriculum";
import { inputClass } from "@/components/AuthLayout";
import { apiFetch, getToken } from "@/api";
import AcademicContextBar from "@/components/AcademicContextBar";
import UserAvatar from "@/components/UserAvatar";
import PageBlobs from "@/components/PageBlobs";
import Paywall from "@/components/Paywall";
import { isPaidPlan } from "@/components/SessionDownload";

const CORE_SUBJECTS = ["Mathematics", "Physics", "Chemistry", "Biology"];
const BOARDS = ["CBSE", "CISCE"];
const CLASSES = ["9", "10", "11", "12"];
const FREE_LIMIT = 2; // guests: 2 tries; logged-in free users: 1 lifetime (server-enforced)
const DAY_MS = 24 * 60 * 60 * 1000;

const getUsage = () => {
  try {
    const u = JSON.parse(localStorage.getItem("abhyas_usage") || "{}");
    if (!u.start || Date.now() - u.start > DAY_MS)
      return { start: Date.now(), count: 0 };
    return u;
  } catch {
    return { start: Date.now(), count: 0 };
  }
};

const PracticeSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const preselectSubject = location.state?.subject || null;
  const quickStart = location.state?.quickStart || null;

  const [board, setBoard] = useState(null);
  const [klass, setKlass] = useState(null);
  const [subject, setSubject] = useState(preselectSubject);
  const [chapter, setChapter] = useState(null);
  const [difficulty, setDifficulty] = useState("Medium");
  const [topic, setTopic] = useState(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [streak, setStreak] = useState(0);
  const [me, setMe] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [autoReady, setAutoReady] = useState(false);
  const autoGenDone = useRef(false);

  const chapters =
    board && klass && subject ? getChapters(board, klass, subject) : [];
  const subjectOptions =
    board && klass ? getSubjects(board, klass) : CORE_SUBJECTS;
  const ready = Boolean(board && klass && subject && chapter);

  // "Practice again" from history: prefill exact session criteria, then auto-generate
  useEffect(() => {
    if (!quickStart) return;
    setBoard(quickStart.board === "CBSE" ? "CBSE" : "CISCE");
    setKlass(String(quickStart.klass));
    setSubject(quickStart.subject);
    setChapter(quickStart.chapter);
    setDifficulty(quickStart.difficulty || "Medium");
    setTopic(quickStart.topic || null);
    setAutoReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (autoReady && ready && !generating && !autoGenDone.current) {
      autoGenDone.current = true;
      handleGenerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoReady, ready]);

  useEffect(() => {
    if (!getToken()) return;
    apiFetch("/me")
      .then((u) => {
        setMe(u);
        setStreak(u.currentStreak ?? 0);
        if (u.board && u.klass && !quickStart) {
          const b = u.board === "CBSE" ? "CBSE" : "CISCE";
          const k = String(u.klass);
          // Board/Class come from the profile; subject is the student's
          // per-feature choice (never auto-assumed from the profile)
          const s = preselectSubject || null;
          setBoard(b);
          setKlass(k);
          setSubject(s);
          setChapter(s ? getChapters(b, k, s)[0] : null);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickBoard = (b) => {
    setBoard(b);
    if (klass && subject && !getSubjects(b, klass).includes(subject))
      setSubject(null);
    if (klass && subject && getSubjects(b, klass).includes(subject))
      setChapter(getChapters(b, klass, subject)[0]);
    if (subject && !getSubjects(b, klass || "9").includes(subject)) setChapter(null);
  };
  const pickClass = (c) => {
    setKlass(c);
    if (board && subject && !getSubjects(board, c).includes(subject))
      setSubject(null);
    if (board && subject && getSubjects(board, c).includes(subject))
      setChapter(getChapters(board, c, subject)[0]);
    if (subject && board && !getSubjects(board, c).includes(subject)) setChapter(null);
  };
  const pickSubject = (s) => {
    setSubject(s);
    if (board && klass) setChapter(getChapters(board, klass, s)[0]);
  };

  // "Change Data" saved a new Board/Class to the global profile — refresh
  // lists and drop selections that are no longer valid (spec §6)
  const handleContextChange = (newBoard, newKlass) => {
    setBoard(newBoard === "CBSE" ? "CBSE" : "CISCE");
    setKlass(String(newKlass));
    setSubject(null);
    setChapter(null);
    setTopic(null);
  };

  const handleGenerate = async () => {
    if (getToken()) {
      try {
        const usage = await apiFetch("/generations/consume", { method: "POST" });
        setMe((prev) =>
          prev
            ? { ...prev, generationsToday: usage.generationsToday, generationLimit: usage.limit }
            : prev
        );
      } catch (err) {
        if (err.message === "FREE_LIMIT_REACHED") {
          setPaywallOpen(true);
          return;
        }
      }
      try {
        const data = await apiFetch("/onboarding", {
          method: "POST",
          body: {
            board: board === "CISCE" ? "ICSE/ISC" : board,
            klass,
          },
        });
        saveProfile({
          board: data.user.board,
          klass: data.user.klass,
        });
      } catch {
        saveProfile({ board: board === "CISCE" ? "ICSE/ISC" : board, klass });
      }
    } else {
      const usage = getUsage();
      if (usage.count >= FREE_LIMIT) {
        setPaywallOpen(true);
        return;
      }
      localStorage.setItem(
        "abhyas_usage",
        JSON.stringify({ start: usage.start, count: usage.count + 1 })
      );
    }
    if (getToken()) {
      setGenerating(true);
      try {
        const gen = await apiFetch("/generate-questions", {
          method: "POST",
          body: {
            board: board === "CISCE" ? "ICSE/ISC" : board,
            klass,
            subject,
            chapter,
            topic: topic || undefined,
            difficulty,
            count: 5,
            chapterList: chapters,
          },
        });
        navigate("/practice", {
          state: {
            subject,
            chapter,
            topic: topic || undefined,
            difficulty,
            klass,
            board,
            questions: gen.questions,
          },
        });
        return;
      } catch (err) {
        toast.error(err.message);
        setGenerating(false);
        return;
      }
    }
    navigate("/practice", { state: { subject, chapter, difficulty, klass, board } });
  };

  const pill = (active) =>
    `rounded-full border-2 px-4 py-2.5 text-xs font-bold transition-[border-color,background-color] duration-200 ${
      active
        ? "border-[#16A34A] bg-[#16A34A]/10 text-[#17201A]"
        : "border-[#17201A]/10 bg-[#F8FAF7] text-[#17201A]/60 hover:border-[#16A34A]/40"
    }`;

  return (
    <div
      data-testid="practice-setup-page"
      className="relative min-h-screen overflow-hidden bg-[#F8FAF7] font-body text-[#17201A]"
    >
      <PageBlobs />
      <header className="relative mx-auto flex max-w-3xl items-center justify-between px-6 py-6">
        <div className="flex items-center gap-4">
          <button
            data-testid="setup-back-btn"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#E3ECE6] bg-white px-4 py-2 text-xs font-bold text-[#17201A] transition-[border-color] duration-200 hover:border-[#FACC15]"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Home
          </button>
          <Link
            to="/"
            data-testid="logo-home-link"
            className="font-display text-2xl tracking-tight"
          >
            <img src="/logo.png" alt="Abhyas" className="h-10 w-auto" />
          </Link>
        </div>
        <div className="flex items-center gap-3">
          <span
            data-testid="setup-streak-badge"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#FACC15]/25 px-3.5 py-1.5 text-xs font-bold text-[#17201A]"
          >
            <Flame className="h-3.5 w-3.5 text-[#FACC15]" />
            {streak}
          </span>
          <UserAvatar />
        </div>
      </header>

      <main className="relative mx-auto max-w-md px-6 pb-16 pt-4">
        <h1 className="font-display text-3xl font-extrabold sm:text-4xl">Set up your practice</h1>
        <p className="mt-1.5 text-sm text-[#17201A]/60">
          Pick a chapter and difficulty — we'll do the rest.
        </p>

        <div className="mt-8 rounded-[2rem] border border-[#E3ECE6] bg-white p-7 shadow-sm sm:p-8">
          {me?.board && me?.klass ? (
            <>
              <label className="mb-1.5 block text-sm font-bold">Academic Context</label>
              <div className="mb-5">
                <AcademicContextBar
                  board={board === "CISCE" ? "ICSE/ISC" : board}
                  klass={klass}
                  onChanged={handleContextChange}
                  testId="practice-context"
                />
              </div>
            </>
          ) : (
            <>
              <label className="mb-1.5 block text-sm font-bold">Board</label>
              <div className="mb-5 grid grid-cols-2 gap-2">
                {BOARDS.map((b) => (
                  <button
                    key={b}
                    data-testid={`board-${b.toLowerCase()}-btn`}
                    onClick={() => pickBoard(b)}
                    className={pill(board === b)}
                  >
                    {b === "CISCE" ? "CISCE (ICSE/ISC)" : b}
                  </button>
                ))}
              </div>

              <label className="mb-1.5 block text-sm font-bold">Class</label>
              <div data-testid="class-selector" className="mb-5 grid grid-cols-4 gap-2">
                {CLASSES.map((c) => (
                  <button
                    key={c}
                    data-testid={`class-${c}-btn`}
                    onClick={() => pickClass(c)}
                    className={pill(klass === c)}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </>
          )}

          <label className="mb-1.5 block text-sm font-bold">Subject</label>
          <div className="mb-5 flex flex-wrap gap-2">
            {subjectOptions.map((s) => (
              <button
                key={s}
                data-testid={`setup-subject-${s.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => pickSubject(s)}
                className={pill(subject === s)}
              >
                {s}
              </button>
            ))}
          </div>

          <label className="mb-1.5 block text-sm font-bold">Chapter</label>
          <select
            data-testid="chapter-select"
            value={chapter || ""}
            onChange={(e) => setChapter(e.target.value)}
            disabled={chapters.length === 0}
            className={`${inputClass} mb-5 appearance-none ${
              chapters.length === 0 ? "cursor-not-allowed opacity-60" : "cursor-pointer"
            }`}
          >
            {chapters.length === 0 && (
              <option value="">Select board, class & subject first</option>
            )}
            {chapters.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <label className="mb-1.5 block text-sm font-bold">Difficulty</label>
          <div data-testid="difficulty-selector" className="mb-7 grid grid-cols-3 gap-2">
            {DIFFICULTIES.map((d) => (
              <button
                key={d}
                data-testid={`difficulty-${d.toLowerCase()}-btn`}
                onClick={() => setDifficulty(d)}
                className={pill(difficulty === d)}
              >
                {d}
              </button>
            ))}
          </div>

          <button
            data-testid="generate-practice-btn"
            data-magnetic="true"
            onClick={handleGenerate}
            disabled={!ready || generating}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#16A34A] py-4 text-base font-bold text-white shadow-sm transition-[background-color,transform,opacity] duration-200 hover:bg-[#15803D] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating questions...
              </>
            ) : (
              <>
                <Wand2 className="h-4 w-4" />
                Generate Practice
              </>
            )}
          </button>
          {me && (
            <p
              data-testid="practice-usage-note"
              className="mt-4 text-center text-xs font-semibold text-[#647067]"
            >
              {Math.max((me.generationLimit ?? 2) - (me.generationsToday ?? 0), 0)} of{" "}
              {me.generationLimit ?? 2}{" "}
              {isPaidPlan(me)
                ? "practice generations left today"
                : "free practice generations left (one-time)"}
            </p>
          )}
        </div>
      </main>
      <Paywall open={paywallOpen} onClose={() => setPaywallOpen(false)} />
    </div>
  );
};

export default PracticeSetup;
